/**
 * The tenancy contract (PLAN §11, D13, D14): who the caller is, which tenant
 * they act in, and the tenant-admin surface — users, groups, memberships — plus
 * the vendor's tenant registry. The routes are `tenancyRoutes.ts`, part of the
 * one route table (`routes.ts`).
 *
 * Every request and response body is `.strict()` (CLAUDE.md → the contract),
 * and every id embedded in a DynamoDB key is constrained to a character set
 * without `#`, the key separator, so an id can never forge a key segment
 * (`docs/data-model.md`).
 *
 * **The tenant never appears in a request body.** It comes only from the
 * verified token (D13). `tenantId` in `createTenantRequestWireSchema` is the id of
 * the tenant being CREATED by the vendor, not the tenant the caller acts in.
 *
 * A group's scope is the domain's `GrantScope` (`@acp/domain/access.js`), and
 * a caller's grants are the domain's `PermissionGrant[]`, so the one pure
 * `isPermitted` check runs in the cloud authorizer and on the node alike.
 */
import type { GrantScope, PermissionGrant } from '@acp/domain/access.js';
import { openEnum } from './openEnum.js';
import { z } from 'zod';
import { permissionIdOpenWireSchema, permissionIdWireSchema, PERMISSION_IDS, type PermissionIdWire } from './permissions.js';
import { entityIdWireSchema, tenantIdWireSchema, userIdWireSchema } from './primitives.js';

export const emailWireSchema = z.email().max(254);

/**
 * Where a group's permissions apply (D14): the whole tenant, or named sites
 * and studios — the domain's `GrantScope`.
 */
export const grantScopeWireSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tenant') }).strict(),
  z
    .object({
      kind: z.literal('resources'),
      siteIds: z.array(entityIdWireSchema).max(200),
      studioIds: z.array(entityIdWireSchema).max(500),
    })
    .strict(),
]);

const permissionListSchema = z.array(permissionIdWireSchema).max(PERMISSION_IDS.length);

/** One group's contribution to a caller — the domain's `PermissionGrant`, with known ids. */
export const permissionGrantWireSchema = z.object({ permissions: permissionListSchema, scope: grantScopeWireSchema }).strict();

/**
 * Permissions as a reader receives them: open, so a newer cloud's new permission reads as `unknown` (`knownPermissions`).
 * Bounded by what a newer release may hold, not by this build's count (a request's list stays bounded by it): a group
 * with every permission of a release that added one must still read.
 */
const MAX_PERMISSIONS_READ = 128;
const permissionOpenListSchema = z.array(permissionIdOpenWireSchema).max(MAX_PERMISSIONS_READ);

/**
 * A grant as `GET /me` answers it: the same, with the permission ids open. Still a domain `PermissionGrant` (its ids
 * are strings): an `unknown` id matches no permission a check asks for, so it grants nothing.
 */
export const permissionGrantOpenWireSchema = z.object({ permissions: permissionOpenListSchema, scope: grantScopeWireSchema }).strict();
const openGrantFitsDomain: z.infer<typeof permissionGrantOpenWireSchema> extends PermissionGrant ? true : false = true;

// A parsed contract value must BE a domain value, or the shared check would be
// checking something else. Compile-time proofs: these lines stop compiling if
// either shape drifts from `@acp/domain/access.js`.
const scopeFitsDomain: z.infer<typeof grantScopeWireSchema> extends GrantScope ? true : false = true;
const grantFitsDomain: z.infer<typeof permissionGrantWireSchema> extends PermissionGrant ? true : false = true;
const domainKindsCovered: GrantScope['kind'] extends z.infer<typeof grantScopeWireSchema>['kind'] ? true : false = true;
export const DOMAIN_SHAPE_PROOFS = [scopeFitsDomain, grantFitsDomain, domainKindsCovered, openGrantFitsDomain] as const;

const groupNameSchema = z.string().trim().min(1).max(100);

/** Every tenant's Admin group, and the vendor tenant's Vendor Admin group (D14, D27). */
export const ADMIN_GROUP_ID = 'grp-admin';
export const VENDOR_ADMIN_GROUP_ID = 'grp-vendor-admin';

/**
 * Groups a tenant may not delete, and the permissions (tenant-wide) they must
 * keep: without them nobody could administer the tenant, or the vendor. The
 * server's fence (`apps/cloud-api/src/routes/groups.ts`) and the SPA's group
 * editor (its locked checkboxes) read this one list.
 */
export function protectedPermissions(groupId: string): readonly PermissionIdWire[] | null {
  if (groupId === ADMIN_GROUP_ID) return ['tenant.admin'];
  if (groupId === VENDOR_ADMIN_GROUP_ID) return ['vendor.admin', 'tenant.admin'];
  return null;
}

// ─── resources ───

export const tenantWireSchema = z.object({ tenantId: tenantIdWireSchema, name: z.string(), createdAt: z.string() }).strict();
export type TenantWire = z.infer<typeof tenantWireSchema>;

/**
 * Where a user stands (7b): `invited` — created, the invite not yet accepted
 * (no password of their own); `active`; `disabled` — deactivated by an admin:
 * Cognito refuses their sign-in, the API refuses their tokens, and the nodes'
 * directories drop them at the next sync (D20). Users written before this
 * field existed read as `active`.
 */
/** Open: a user's status as a listing shows it. */
export const userStatusWireSchema = openEnum(['invited', 'active', 'disabled']);
export type UserStatusWire = z.infer<typeof userStatusWireSchema>;

export const userWireSchema = z
  .object({
    userId: userIdWireSchema,
    email: emailWireSchema,
    createdAt: z.string(),
    status: userStatusWireSchema,
    /** When the latest invite email was sent, or null (a user made before invites, or by the dev seed). */
    invitedAt: z.string().nullable(),
  })
  .strict();
export type UserWire = z.infer<typeof userWireSchema>;

/** A user in the admin list: with the ids of their groups. */
export const userSummaryWireSchema = userWireSchema.extend({ groupIds: z.array(entityIdWireSchema) }).strict();
export type UserSummaryWire = z.infer<typeof userSummaryWireSchema>;

export const groupWireSchema = z
  .object({
    groupId: entityIdWireSchema,
    name: z.string(),
    /** Open: a newer cloud may grant a permission this reader does not know (an editor then keeps it: `knownPermissions`). */
    permissions: permissionOpenListSchema,
    scope: grantScopeWireSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type GroupWire = z.infer<typeof groupWireSchema>;

// ─── path parameters ───

export const userPathWireSchema = z.object({ userId: userIdWireSchema.describe('The user’s id (their Cognito `sub`).') }).strict();
export const groupPathWireSchema = z.object({ groupId: entityIdWireSchema.describe('The group’s id.') }).strict();
export const memberPathWireSchema = groupPathWireSchema.extend({ userId: userIdWireSchema.describe('The member’s user id.') }).strict();

// ─── requests ───

export const createTenantRequestWireSchema = z
  .object({
    tenantId: tenantIdWireSchema,
    name: z.string().trim().min(1).max(200),
    /** The tenant's first admin: created in Cognito (invitation suppressed) and put in the Admin group. */
    adminEmail: emailWireSchema,
  })
  .strict();
export type CreateTenantRequestWire = z.infer<typeof createTenantRequestWireSchema>;

export const createUserRequestWireSchema = z
  .object({
    email: emailWireSchema,
    /** Groups to add the new user to; each must exist in the caller's tenant. */
    groupIds: z.array(entityIdWireSchema).max(50).optional(),
  })
  .strict();

export const createGroupRequestWireSchema = z.object({ name: groupNameSchema, permissions: permissionListSchema, scope: grantScopeWireSchema }).strict();

export const updateGroupRequestWireSchema = z
  .object({
    name: groupNameSchema.optional(),
    permissions: permissionListSchema.optional(),
    scope: grantScopeWireSchema.optional(),
    /**
     * Optimistic concurrency (D35): the `updatedAt` the editor loaded. When it no longer matches, the update is
     * `conflict` and changes nothing. Absent: last writer wins (the CLI).
     */
    ifUpdatedAt: z.string().max(64).optional(),
  })
  .strict()
  .refine((body) => body.name !== undefined || body.permissions !== undefined || body.scope !== undefined, {
    message: 'a group update must change at least one of name, permissions, scope',
  });

// ─── responses ───

export const meResponseWireSchema = z
  .object({
    user: z.object({ userId: userIdWireSchema, email: emailWireSchema }).strict(),
    tenant: z
      .object({
        tenantId: tenantIdWireSchema,
        name: z.string(),
        /**
         * The staff tenant (D39: the vendor's own): its users work in the console host, and the customer app sends them
         * there. Absent from a cloud before phase 7f: read as false.
         */
        staff: z.boolean().optional(),
      })
      .strict(),
    /** Every permission held somewhere in the tenant, sorted. */
    permissions: z.array(permissionIdOpenWireSchema),
    /** Where each applies: one entry per group, as the authorizer resolved them (D14). */
    grants: z.array(permissionGrantOpenWireSchema),
  })
  .strict();
export type MeResponseWire = z.infer<typeof meResponseWireSchema>;

export const createdTenantResponseWireSchema = z.object({ tenant: tenantWireSchema, adminUser: userWireSchema }).strict();
export const listTenantsResponseWireSchema = z.object({ tenants: z.array(tenantWireSchema) }).strict();
export const userResponseWireSchema = z.object({ user: userWireSchema }).strict();
export const listUsersResponseWireSchema = z.object({ users: z.array(userSummaryWireSchema) }).strict();

/**
 * How an invite email left (7b): `email` — handed to SES; `file` — the
 * development fallback wrote it to a file (`ACP_MAIL_DIR`); `none` — no mailer
 * is configured on this stack, so nothing was sent (resend once one is);
 * `failed` — the transport refused it (SES's sandbox refuses unverified
 * recipients): the user exists, resend once the cause is fixed.
 */
/** Open: how an invite left, as the cloud reports it. */
const inviteDeliveryWireSchema = openEnum(['email', 'file', 'none', 'failed']);
export type InviteDeliveryWire = z.infer<typeof inviteDeliveryWireSchema>;

export const inviteSummaryWireSchema = z.object({ expiresAt: z.string(), delivery: inviteDeliveryWireSchema }).strict();

/** `POST /users` and `POST /users/{userId}/invite`: the user and the invite just sent (never its link). */
export const invitedUserResponseWireSchema = z.object({ user: userWireSchema, invite: inviteSummaryWireSchema }).strict();

// ─── accepting an invite (public: the link's token is the credential) ───

/**
 * The invite link's token: `ACPI.<inviteId>.<secret>`, carried in the link's
 * URL fragment (never sent to a server by the browser) and posted in a body —
 * never a path or query, which land in access logs.
 */
const inviteTokenWireSchema = z.string().min(1).max(256);

/** Cognito's pool policy (infrastructure/cognito.tf): 12+ characters with lower case, upper case and a digit. */
export const newPasswordWireSchema = z
  .string()
  .min(12, 'at least 12 characters')
  .max(256)
  .regex(/[a-z]/, 'a lower-case letter')
  .regex(/[A-Z]/, 'an upper-case letter')
  .regex(/[0-9]/, 'a digit');

export const inspectInviteRequestWireSchema = z.object({ token: inviteTokenWireSchema }).strict();
export const inspectInviteResponseWireSchema = z.object({ email: emailWireSchema, tenantName: z.string(), expiresAt: z.string() }).strict();
export type InspectInviteResponseWire = z.infer<typeof inspectInviteResponseWireSchema>;

export const acceptInviteRequestWireSchema = z.object({ token: inviteTokenWireSchema, password: newPasswordWireSchema }).strict();
export const acceptInviteResponseWireSchema = z.object({ email: emailWireSchema }).strict();
export const groupResponseWireSchema = z.object({ group: groupWireSchema }).strict();
export const groupWithMembersResponseWireSchema = z.object({ group: groupWireSchema, memberIds: z.array(userIdWireSchema) }).strict();
export const listGroupsResponseWireSchema = z.object({ groups: z.array(groupWireSchema) }).strict();
